import { useEffect, useState } from "react";

/** До 10 цифр после кода страны → отображение с префиксом +7 */
function formatPhoneWithPlus7(nationalDigits) {
  const d = String(nationalDigits).replace(/\D/g, "").slice(0, 10);
  if (!d) return "+7 ";
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 8);
  const e = d.slice(8, 10);
  let out = `+7 ${a}`;
  if (b) out += ` ${b}`;
  if (c) out += `-${c}`;
  if (e) out += `-${e}`;
  return out;
}

/** Из значения инпута — только 10 цифр «национальной» части (без ведущей 7). */
function parseNational10FromInput(raw) {
  let d = String(raw ?? "").replace(/\D/g, "");
  if (d.startsWith("8") && d.length >= 10) d = `7${d.slice(1)}`;
  if (d.startsWith("7")) d = d.slice(1);
  return d.slice(0, 10);
}
import { Navigate, useNavigate } from "react-router-dom";
import {
  fetchAccountMe,
  fetchAccountParticipationsMapped,
  marketplacePinHasPin,
  marketplacePinLogin,
  marketplacePinRequestOtp,
  marketplacePinResetConfirm,
  marketplacePinResetRequestOtp,
  marketplacePinSet,
  marketplacePinVerifyOtp,
  marketplacePasskeyLoginBegin,
  marketplacePasskeyLoginFinish,
  marketplacePasskeyRegisterBegin,
  marketplacePasskeyRegisterFinish,
} from "../api/client";
import {
  cacheGlobalProfileFromAccountMe,
  clearMaxWebAppSessionFlag,
  clearTelegramWebAppSessionFlag,
  isVitrineLoggedIn,
  normalizePhoneDigits,
  saveMarketplaceTokens,
} from "../lib/wicoopProfileStorage";
import {
  ensureTelegramProfileAuth,
  isInsideTelegramWebApp,
} from "../lib/telegramWebAppAuth.js";
import { navigateAfterAuth } from "../utils/profileCompletion.js";
import { ArrowLeft, Backspace, Fingerprint } from "@phosphor-icons/react";
import styles from "./ProfileLoginPage.module.css";

function base64urlToBuffer(b64url) {
  const s = String(b64url || "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufferToBase64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  return b64;
}

function normalizeCredentialForServer(cred) {
  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
      authenticatorData: bufferToBase64url(cred.response.authenticatorData),
      signature: bufferToBase64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufferToBase64url(cred.response.userHandle) : null,
    },
  };
}

function normalizeAttestationForServer(cred) {
  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
      attestationObject: bufferToBase64url(cred.response.attestationObject),
    },
  };
}

export default function ProfileLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("+7 ");
  const [phoneConfirm, setPhoneConfirm] = useState("+7 ");
  /** Номер, подтверждённый на шаге телефона — не теряется на шаге PIN. */
  const [authPhoneDigits, setAuthPhoneDigits] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  /** false: сразу после открытия страницы пробуем Face ID / passkey; true: показать телефон и дальше */
  const [passkeyIntroDone, setPasskeyIntroDone] = useState(false);
  /** Пропуск шага «Повторите номер» (вход по PIN / код на почту из профиля / сброс PIN с почтой в профиле). */
  const [skippedPhoneConfirm, setSkippedPhoneConfirm] = useState(false);
  /** Код на почту отправлен на email из профиля (пользователь не вводил адрес). */
  const [otpFromProfileEmail, setOtpFromProfileEmail] = useState(false);
  const [resetOtpFromProfileEmail, setResetOtpFromProfileEmail] = useState(false);
  const [telegramAuthPending, setTelegramAuthPending] = useState(false);
  const inTelegram = isInsideTelegramWebApp();

  useEffect(() => {
    if (isVitrineLoggedIn() || inTelegram) {
      if (inTelegram) setPasskeyIntroDone(true);
      return undefined;
    }
    let cancelled = false;
    let loggedIn = false;

    (async () => {
      if (!window.PublicKeyCredential || !navigator.credentials) {
        if (!cancelled) setPasskeyIntroDone(true);
        return;
      }
      setPasskeyBusy(true);
      try {
        const begin = await marketplacePasskeyLoginBegin({});
        const publicKey = { ...begin.publicKey };
        publicKey.challenge = base64urlToBuffer(publicKey.challenge);
        const ac = publicKey.allowCredentials;
        if (Array.isArray(ac) && ac.length > 0) {
          publicKey.allowCredentials = ac.map((c) => ({
            ...c,
            id: base64urlToBuffer(c.id),
          }));
        }
        const assertion = await navigator.credentials.get({ publicKey });
        const payload = normalizeCredentialForServer(assertion);
        const fin = await marketplacePasskeyLoginFinish({
          token: begin.token,
          credential: payload,
        });
        if (fin?.access && fin?.refresh) {
          loggedIn = true;
          clearMaxWebAppSessionFlag();
          clearTelegramWebAppSessionFlag();
          saveMarketplaceTokens({ access: fin.access, refresh: fin.refresh });
          navigate("/profile", { replace: true });
        }
      } catch {
        /* отмена, нет ключа или ошибка — показываем ввод телефона */
      } finally {
        if (!cancelled) setPasskeyBusy(false);
        if (!cancelled && !loggedIn) setPasskeyIntroDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (isVitrineLoggedIn()) {
    return <Navigate to="/profile" replace />;
  }

  async function loginViaTelegram() {
    if (!inTelegram || telegramAuthPending) return;
    setTelegramAuthPending(true);
    setError(null);
    try {
      const r = await ensureTelegramProfileAuth();
      if (r.ok) {
        await navigateAfterAuth(navigate);
      } else if (r.denied) {
        setError("Нужно поделиться номером телефона, чтобы войти.");
      } else {
        setError(r.reason || "Не удалось войти через Telegram");
      }
    } finally {
      setTelegramAuthPending(false);
    }
  }

  const phoneDigits = normalizePhoneDigits(authPhoneDigits || phone);
  const phoneConfirmDigits = normalizePhoneDigits(phoneConfirm);
  const phoneOk = phoneDigits.length >= 10;
  const phonesMatch = phoneOk && phoneConfirmDigits === phoneDigits;
  const emailTrim = email.trim();
  const emailOk = !emailTrim ? false : /^\S+@\S+\.\S+$/.test(emailTrim);

  function pinDigits() {
    return String(pin ?? "").replace(/\D/g, "").slice(0, 4);
  }
  function setPinDigits(next) {
    setPin(String(next ?? "").replace(/\D/g, "").slice(0, 4));
  }
  function pin2Digits() {
    return String(pin2 ?? "").replace(/\D/g, "").slice(0, 4);
  }
  function setPin2Digits(next) {
    setPin2(String(next ?? "").replace(/\D/g, "").slice(0, 4));
  }

  function onKeypadDigit(d) {
    if (step === "pin" || step === "set_pin" || step === "reset_pin") {
      const cur = pinDigits();
      if (cur.length < 4) setPinDigits(cur + String(d));
      return;
    }
    if (step === "set_pin2") {
      const cur = pin2Digits();
      if (cur.length < 4) setPin2Digits(cur + String(d));
    }
  }

  function onKeypadBackspace() {
    if (step === "set_pin2") return setPin2Digits(pin2Digits().slice(0, -1));
    if (step === "pin" || step === "set_pin" || step === "reset_pin") {
      return setPinDigits(pinDigits().slice(0, -1));
    }
  }

  async function continueFromPhone() {
    setError(null);
    if (!phoneOk) {
      setError("Введите номер телефона (не менее 10 цифр).");
      return;
    }
    const digits = normalizePhoneDigits(phone);
    setAuthPhoneDigits(digits);
    setResetMode(false);
    setBusy(true);
    try {
      const data = await marketplacePinHasPin({ phone: digits });
      if (data?.has_pin) {
        setSkippedPhoneConfirm(true);
        setOtpFromProfileEmail(false);
        setPinDigits("");
        setStep("pin");
        return;
      }
      if (data?.has_email) {
        setSkippedPhoneConfirm(true);
        setOtpFromProfileEmail(true);
        setOtpCode("");
        await marketplacePinRequestOtp({ phone: phoneDigits });
        setStep("otp");
        return;
      }
      setSkippedPhoneConfirm(false);
      setOtpFromProfileEmail(false);
      setPhoneConfirm("+7 ");
      setStep("phone_confirm");
    } catch (e) {
      setError(e.message ?? "Не удалось продолжить");
    } finally {
      setBusy(false);
    }
  }

  async function continueFromPhoneConfirm() {
    setError(null);
    if (!phonesMatch) {
      setError("Номера не совпадают. Введите ещё раз.");
      return;
    }
    setAuthPhoneDigits(phoneDigits);
    setBusy(true);
    try {
      if (resetMode) {
        if (!emailOk) setEmail("");
        setResetOtpFromProfileEmail(false);
        setStep("email_reset");
        return;
      }
      const data = await marketplacePinHasPin({ phone: phoneDigits });
      if (data?.has_pin) {
        setSkippedPhoneConfirm(false);
        setPinDigits("");
        setStep("pin");
        return;
      }
      setEmail("");
      setOtpFromProfileEmail(false);
      setStep("email_request");
    } catch (e) {
      setError(e.message ?? "Не удалось продолжить");
    } finally {
      setBusy(false);
    }
  }

  async function sendLoginEmailCode() {
    setError(null);
    if (!emailOk) {
      setError("Укажите корректный email.");
      return;
    }
    setEmailBusy(true);
    try {
      setOtpFromProfileEmail(false);
      setOtpCode("");
      await marketplacePinRequestOtp({ phone: phoneDigits, email: emailTrim });
      setStep("otp");
    } catch (e) {
      setError(e.message ?? "Не удалось отправить письмо");
    } finally {
      setEmailBusy(false);
    }
  }

  async function startResetPin() {
    setError(null);
    if (!phoneOk) {
      setError("Введите номер телефона (не менее 10 цифр).");
      return;
    }
    const digits = normalizePhoneDigits(phone);
    setAuthPhoneDigits(digits);
    setResetMode(true);
    setBusy(true);
    try {
      const data = await marketplacePinHasPin({ phone: digits });
      if (!data?.has_pin) {
        setError("Для этого номера PIN не установлен.");
        setResetMode(false);
        return;
      }
      if (data?.has_email) {
        setSkippedPhoneConfirm(true);
        setResetOtpFromProfileEmail(true);
        setOtpCode("");
        setPin("");
        await marketplacePinResetRequestOtp({ phone: phoneDigits });
        setStep("reset_pin");
        return;
      }
      setSkippedPhoneConfirm(false);
      setResetOtpFromProfileEmail(false);
      setPhoneConfirm("+7 ");
      setStep("phone_confirm");
    } catch (e) {
      setError(e.message ?? "Не удалось продолжить");
      setResetMode(false);
    } finally {
      setBusy(false);
    }
  }

  async function sendResetEmailCode() {
    setError(null);
    if (!emailOk) {
      setError("Укажите корректный email.");
      return;
    }
    setEmailBusy(true);
    try {
      setResetOtpFromProfileEmail(false);
      setOtpCode("");
      setPin("");
      await marketplacePinResetRequestOtp({ phone: phoneDigits, email: emailTrim });
      setStep("reset_pin");
    } catch (e) {
      setError(e.message ?? "Не удалось отправить письмо");
    } finally {
      setEmailBusy(false);
    }
  }

  async function verifyOtp() {
    setError(null);
    const digits = String(otpCode ?? "").replace(/\D/g, "");
    if (digits.length !== 4) {
      setError("Введите 4 цифры из письма.");
      return;
    }
    if (!otpFromProfileEmail && !emailOk) {
      setError("Укажите корректный email на предыдущем шаге.");
      return;
    }
    setBusy(true);
    try {
      const payload = { phone: phoneDigits, code: digits };
      if (emailTrim) payload.email = emailTrim;
      const data = await marketplacePinVerifyOtp(payload);
      if (data?.needs_pin_setup) {
        setOtpToken(String(data.otp_token || ""));
        // Не очищаем email: он уже введён на шаге до кода и нужен для set_pin на сервере.
        setPinDigits("");
        setPin2Digits("");
        setStep("set_pin");
      } else {
        setStep("phone");
        throw new Error("PIN уже установлен. Введите PIN для входа.");
      }
    } catch (e) {
      setError(e.message ?? "Неверный код");
    } finally {
      setBusy(false);
    }
  }

  async function setPinAndLogin() {
    setError(null);
    const p1 = pinDigits();
    const p2 = pin2Digits();
    if (p1.length !== 4) {
      setError("PIN должен состоять из 4 цифр.");
      return;
    }
    if (p1 !== p2) {
      setError("PIN и подтверждение не совпадают.");
      return;
    }
    if (!otpFromProfileEmail && !emailOk) {
      setError("Укажите корректный email.");
      return;
    }
    setBusy(true);
    try {
      const body = { otp_token: otpToken, pin: p1 };
      if (emailTrim) body.email = emailTrim;
      const data = await marketplacePinSet(body);
      if (data?.access && data?.refresh) {
        clearMaxWebAppSessionFlag();
        clearTelegramWebAppSessionFlag();
        saveMarketplaceTokens({ access: data.access, refresh: data.refresh });
        // Предложим настроить passkey сразу после первого входа.
        try {
          await setupPasskeyAfterLogin();
        } catch {
          /* ignore */
        }
        try {
          const me = await fetchAccountMe();
          cacheGlobalProfileFromAccountMe(me);
          await fetchAccountParticipationsMapped();
        } catch {
          /* догрузится на странице профиля */
        }
        navigate("/profile", { replace: true });
      } else {
        throw new Error("Сервер не выдал сессию. Обновите приложение.");
      }
    } catch (e) {
      setError(e.message ?? "Не удалось установить PIN");
    } finally {
      setBusy(false);
    }
  }

  async function loginByPin() {
    setError(null);
    const p = pinDigits();
    if (!phoneOk) {
      setError("Введите номер телефона (не менее 10 цифр).");
      return;
    }
    if (p.length !== 4) {
      setError("PIN должен состоять из 4 цифр.");
      return;
    }
    setBusy(true);
    try {
      const data = await marketplacePinLogin({ phone: phoneDigits, pin: p });
      if (data?.access && data?.refresh) {
        clearMaxWebAppSessionFlag();
        clearTelegramWebAppSessionFlag();
        saveMarketplaceTokens({ access: data.access, refresh: data.refresh });
        // Можно настроить passkey после входа по PIN (по желанию пользователя).
        try {
          const me = await fetchAccountMe();
          cacheGlobalProfileFromAccountMe(me);
          await fetchAccountParticipationsMapped();
        } catch {
          /* догрузится на странице профиля */
        }
        navigate("/profile", { replace: true });
      } else {
        throw new Error("Сервер не выдал сессию. Обновите приложение.");
      }
    } catch (e) {
      setError(e.message ?? "Не удалось войти");
    } finally {
      setBusy(false);
    }
  }

  async function setupPasskeyAfterLogin() {
    // Вызываем после успешного входа (по PIN или после установки PIN).
    setError(null);
    if (!window.PublicKeyCredential || !navigator.credentials) {
      setError("Passkeys не поддерживаются в этом браузере.");
      return;
    }
    setPasskeyBusy(true);
    try {
      const begin = await marketplacePasskeyRegisterBegin({ phone: phoneDigits });
      const publicKey = begin.publicKey;
      publicKey.challenge = base64urlToBuffer(publicKey.challenge);
      publicKey.user.id = base64urlToBuffer(publicKey.user.id);
      if (Array.isArray(publicKey.excludeCredentials)) {
        publicKey.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
          ...c,
          id: base64urlToBuffer(c.id),
        }));
      }
      const att = await navigator.credentials.create({ publicKey });
      const payload = normalizeAttestationForServer(att);
      await marketplacePasskeyRegisterFinish({
        phone: phoneDigits,
        token: begin.token,
        credential: payload,
        label: "Face ID",
      });
      setError(null);
    } catch (e) {
      setError(e.message ?? "Не удалось настроить Passkey");
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function confirmResetPin() {
    setError(null);
    const c = String(otpCode ?? "").replace(/\D/g, "");
    const p = pinDigits();
    if (c.length !== 4) {
      setError("Введите 4 цифры из письма.");
      return;
    }
    if (p.length !== 4) {
      setError("PIN должен состоять из 4 цифр.");
      return;
    }
    if (!resetOtpFromProfileEmail && !emailOk) {
      setError("Укажите email на шаге с письмом.");
      return;
    }
    setBusy(true);
    try {
      const body = { phone: phoneDigits, code: c, new_pin: p };
      if (emailTrim) body.email = emailTrim;
      const data = await marketplacePinResetConfirm(body);
      if (data?.access && data?.refresh) {
        clearMaxWebAppSessionFlag();
        clearTelegramWebAppSessionFlag();
        saveMarketplaceTokens({ access: data.access, refresh: data.refresh });
        navigate("/profile", { replace: true });
      } else {
        throw new Error("Сервер не выдал сессию. Обновите приложение.");
      }
    } catch (e) {
      setError(e.message ?? "Не удалось сбросить PIN");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <section className={styles.card} aria-label="Форма входа">
        {step === "phone" && !passkeyIntroDone && (
          <div className={styles.passkeyIntroWrap}>
            <div className={styles.passkeySpinner} aria-hidden />
            <p className={styles.passkeyIntro} role="status" aria-live="polite">
              {passkeyBusy ? "Вход по Face ID…" : "Запуск входа…"}
            </p>
          </div>
        )}

        {step === "phone" && passkeyIntroDone && (
          <>
            <p className={styles.formStepLead}>Вход в аккаунт</p>
            <label className={styles.fieldLabel} htmlFor="pl-phone">
              Телефон
            </label>
            <input
              id="pl-phone"
              className={styles.input}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 900 000-00-00"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhone(formatPhoneWithPlus7(parseNational10FromInput(phone)))}
            />
            {inTelegram ? (
              <>
                <div className={styles.authDivider}>
                  <span>или</span>
                </div>
                <button
                  type="button"
                  className={styles.btnTelegram}
                  disabled={telegramAuthPending || busy}
                  onClick={loginViaTelegram}
                >
                  {telegramAuthPending ? "Запрос номера…" : "Войти через Telegram"}
                </button>
                <p className={styles.hint}>
                  Поделитесь номером в Telegram — вход без PIN и SMS.
                </p>
              </>
            ) : null}
            <p className={styles.hint} style={{ marginTop: 12, textAlign: "center" }}>
              Продолжая, вы принимаете условия{" "}
              <a
                href="https://wicoop.ru/cashback-offer"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}
              >
                оферты
              </a>
              .
            </p>
          </>
        )}

        {step === "phone_confirm" && (
          <>
            <p className={styles.formStepLead}>
              {resetMode ? (
                <>Повторите номер ниже, потом укажите почту — пришлём код для сброса PIN.</>
              ) : (
                <>Кажется, вы у нас впервые. Введите номер ещё раз — так проверим, что всё без опечаток.</>
              )}
            </p>
            <label className={styles.fieldLabel} htmlFor="pl-phone2">
              Повторите номер
            </label>
            <input
              id="pl-phone2"
              className={styles.input}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 900 000-00-00"
              value={phoneConfirm}
              onChange={(e) =>
                setPhoneConfirm(formatPhoneWithPlus7(parseNational10FromInput(e.target.value)))
              }
            />
          </>
        )}

        {step === "email_request" && (
          <>
            <label className={styles.fieldLabel} htmlFor="pl-email-req">
              Email
            </label>
            <input
              id="pl-email-req"
              className={styles.input}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className={styles.hint}>Отправим код для входа на эту почту.</p>
          </>
        )}

        {step === "email_reset" && (
          <>
            <label className={styles.fieldLabel} htmlFor="pl-email-reset">
              Email аккаунта
            </label>
            <input
              id="pl-email-reset"
              className={styles.input}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className={styles.hint}>Код сброса PIN придёт на эту почту.</p>
          </>
        )}

        {step === "pin" && (
          <>
            <label className={styles.fieldLabel}>Введите PIN</label>
            <div className={styles.pinDots} aria-label="PIN">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={i < pinDigits().length ? styles.dotFilled : styles.dot} />
              ))}
            </div>
            <button
              type="button"
              className={styles.pinRecoverLink}
              disabled={busy || emailBusy || !phoneOk}
              onClick={startResetPin}
            >
              Забыли PIN? Восстановить по email
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <label className={styles.fieldLabel} htmlFor="pl-otp">
              Код из письма
            </label>
            <input
              id="pl-otp"
              className={styles.input}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="0000"
              maxLength={4}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            <p className={styles.hint}>
              {otpFromProfileEmail
                ? "Код отправлен на почту, привязанную к этому номеру."
                : `Письмо отправлено на ${emailTrim || "указанный email"}.`}
            </p>
          </>
        )}

        {step === "set_pin" && (
          <>
            <p className={styles.formStepLead}>
              Придумайте код из четырёх цифр — потом по нему можно будет войти быстрее, без почты и кода каждый раз.
            </p>
            <label className={styles.fieldLabel} htmlFor="pl-newpin">
              Придумайте PIN
            </label>
            <div className={styles.pinDots} aria-label="PIN">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={i < pinDigits().length ? styles.dotFilled : styles.dot} />
              ))}
            </div>
          </>
        )}

        {step === "set_pin2" && (
          <>
            <label className={styles.fieldLabel}>Повторите PIN</label>
            <div className={styles.pinDots} aria-label="Повтор PIN">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={i < pin2Digits().length ? styles.dotFilled : styles.dot} />
              ))}
            </div>
            <p className={styles.hint}>Введите PIN ещё раз.</p>
          </>
        )}

        {step === "reset_pin" && (
          <>
            <label className={styles.fieldLabel} htmlFor="pl-resetcode">
              Код из письма
            </label>
            <input
              id="pl-resetcode"
              className={styles.input}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="0000"
              maxLength={4}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            <label className={styles.fieldLabel} htmlFor="pl-resetpin">
              Новый PIN
            </label>
            <div className={styles.pinDots} aria-label="Новый PIN">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={i < pinDigits().length ? styles.dotFilled : styles.dot} />
              ))}
            </div>
            <p className={styles.hint}>Мы заменим PIN после проверки кода из письма.</p>
          </>
        )}

        {error ? (
          <div className={styles.errorBox} role="alert">
            {error}
          </div>
        ) : null}

        {((step === "phone" && passkeyIntroDone) ||
          step === "phone_confirm" ||
          step === "email_request" ||
          step === "email_reset" ||
          step === "otp") && (
          <div
            className={
              (step === "phone" && passkeyIntroDone) || step === "email_request"
                ? `${styles.nav} ${styles.navSingle}`
                : styles.nav
            }
          >
            {step === "phone" && passkeyIntroDone ? (
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={busy || !phoneOk}
                onClick={continueFromPhone}
              >
                Продолжить
              </button>
            ) : step === "phone_confirm" ? (
              <>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setResetMode(false);
                    setStep("phone");
                  }}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={busy || !phonesMatch}
                  onClick={continueFromPhoneConfirm}
                >
                  {busy ? "Проверка…" : "Продолжить"}
                </button>
              </>
            ) : step === "email_request" ? (
              <>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={emailBusy}
                  onClick={() => {
                    setError(null);
                    if (skippedPhoneConfirm) {
                      setSkippedPhoneConfirm(false);
                      setStep("phone");
                    } else {
                      setStep("phone_confirm");
                    }
                  }}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={emailBusy || !emailOk}
                  onClick={sendLoginEmailCode}
                >
                  {emailBusy ? "Отправка…" : "Отправить код на почту"}
                </button>
              </>
            ) : step === "email_reset" ? (
              <>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={emailBusy}
                  onClick={() => {
                    setError(null);
                    if (skippedPhoneConfirm) {
                      setSkippedPhoneConfirm(false);
                      setResetMode(false);
                      setStep("phone");
                    } else {
                      setStep("phone_confirm");
                    }
                  }}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={emailBusy || !emailOk}
                  onClick={sendResetEmailCode}
                >
                  {emailBusy ? "Отправка…" : "Отправить код"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    if (skippedPhoneConfirm) {
                      setSkippedPhoneConfirm(false);
                      setOtpFromProfileEmail(false);
                      setStep("phone");
                    } else {
                      setStep("email_request");
                    }
                  }}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={busy || otpCode.replace(/\D/g, "").length !== 4}
                  onClick={() => verifyOtp()}
                >
                  {busy ? "Проверка…" : "Продолжить"}
                </button>
              </>
            )}
          </div>
        )}

        {(step === "pin" || step === "set_pin" || step === "set_pin2" || step === "reset_pin") && (
          <div className={styles.keypad} aria-label="Цифровая клавиатура">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button
                key={d}
                type="button"
                className={`${styles.keypadBtn} ${styles.keypadBtnDigit}`}
                onClick={() => onKeypadDigit(d)}
                disabled={busy}
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              className={`${styles.keypadBtn} ${styles.keypadBtnIcon}`}
              onClick={onKeypadBackspace}
              disabled={busy}
              aria-label="Удалить цифру"
            >
              <Backspace className={styles.keypadGlyph} size={28} weight="regular" aria-hidden />
            </button>
            <button
              type="button"
              className={`${styles.keypadBtn} ${styles.keypadBtnDigit}`}
              onClick={() => onKeypadDigit("0")}
              disabled={busy}
            >
              0
            </button>
            {step === "reset_pin" ? (
              <button
                type="button"
                className={`${styles.keypadBtn} ${styles.keypadBtnIcon} ${styles.keypadBtnClear}`}
                onClick={() => {
                  setPinDigits("");
                }}
                disabled={busy}
                aria-label="Очистить PIN"
              >
                C
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.keypadBtn} ${styles.keypadBtnIcon} ${styles.keypadBtnPasskey}`}
                disabled={passkeyBusy || busy || !phoneOk}
                onClick={setupPasskeyAfterLogin}
                aria-label={passkeyBusy ? "Настройка Face ID…" : "Настроить Face ID"}
                title="Настроить Face ID"
              >
                <Fingerprint className={styles.keypadPasskeyIcon} size={30} weight="regular" aria-hidden />
              </button>
            )}
          </div>
        )}

        {(step === "pin" || step === "set_pin" || step === "set_pin2" || step === "reset_pin") && (
          <div className={`${styles.nav} ${styles.navKeypad}`}>
            <button
              type="button"
              className={styles.btnNavBack}
              disabled={busy}
              aria-label="Назад"
              onClick={() => {
                setError(null);
                if (step === "pin") {
                  if (skippedPhoneConfirm) {
                    setSkippedPhoneConfirm(false);
                    setStep("phone");
                  } else {
                    setStep("phone_confirm");
                  }
                } else if (step === "set_pin") setStep("otp");
                else if (step === "set_pin2") setStep("set_pin");
                else if (step === "reset_pin") {
                  if (resetOtpFromProfileEmail) {
                    setResetOtpFromProfileEmail(false);
                    setSkippedPhoneConfirm(false);
                    setResetMode(false);
                    setStep("phone");
                  } else {
                    setStep("email_reset");
                  }
                } else setStep("phone");
              }}
            >
              <ArrowLeft size={22} weight="bold" aria-hidden />
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={
                busy ||
                (step === "pin" && pinDigits().length !== 4) ||
                (step === "set_pin" && pinDigits().length !== 4) ||
                (step === "set_pin2" && pin2Digits().length !== 4) ||
                (step === "reset_pin" && (otpCode.replace(/\D/g, "").length !== 4 || pinDigits().length !== 4))
              }
              onClick={() => {
                if (step === "pin") return loginByPin();
                if (step === "set_pin") return setStep("set_pin2");
                if (step === "set_pin2") return setPinAndLogin();
                if (step === "reset_pin") return confirmResetPin();
              }}
            >
              {busy
                ? "Проверка…"
                : step === "pin"
                  ? "Войти"
                  : step === "set_pin"
                    ? "Далее"
                    : step === "set_pin2"
                      ? "Сохранить PIN и войти"
                      : "Сбросить PIN и войти"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
